import { PersonRequest } from "@plinknz/tah-website-elements";

import { aeChoices, choices } from "./config-generics/aeKao";
import { personalDetailsConfig } from "./config-generics/personal-details";
import { signatureConfig } from "./config-generics/signature";
import {
  PageConfig,
  SurveyConfig,
} from "../../components/content-blocks/forms/customer-form-types";

function attendeeConfig({ index }: { index: number }): PageConfig[] {
  const visibleIf = `{attendee${index - 1}Additional} = "Āe"`;
  return [
    {
      name: "attendee",
      title: `Taipitopito - Attendee ${index} Details`,
      visibleIf,
      questions: [
        {
          type: "text",
          name: `attendee${index}Name`,
          title: "Ko wai tōna ingoa?",
          placeHolder: "What is their name?",
          isRequired: true,
        },
        {
          title: "E hia ōna tau?",
          type: "text",
          name: `attendee${index}Age`,
          placeHolder: "What is their age?",
          startWithNewLine: true,
          isRequired: true,
        },
      ],
    },
    {
      name: "matePāwera",
      title: `Mate Pāwera - Attendee ${index} Allergies`,
      visibleIf,
      questions: [
        {
          type: "radiogroup",
          name: `attendee${index}Allergies`,
          title: "Do they have any allergies, including dietary requirements?",
          choices,
          isRequired: true,
        },
        {
          type: "text",
          name: `attendee${index}AllergyDetails`,
          title:
            "Please list all allergies or dietary requirements, as well as medications required.",
          placeHolder: "Mate Pāwera",
          visibleIf: `{attendee${index}Allergies} = 'Āe'`,
          isRequired: true,
        },
      ],
    },
    {
      name: "additionalAttendee",
      title: "He Hunga Anō - Additional Attendee",
      visibleIf,
      questions: [
        {
          type: "radiogroup",
          name: `attendee${index}Additional`,
          title:
            "Would you like to include another person on your registration?",
          choices,
          isRequired: true,
        },
      ],
    },
  ];
}

export const createSurveyConfig = (member: PersonRequest): SurveyConfig => {
  const personalConfig = personalDetailsConfig(member);
  const additionalAttendees = [];
  let index = 2;
  while (index < 21) {
    additionalAttendees.push(...attendeeConfig({ index }));

    index += 1;
  }

  return {
    pages: [
      {
        name: "overview",
        title: "Annual Whānau Kirihimete Party",
        elements: [
          {
            type: "html",
            html: `
<p>
  <table style="border-collapse: separate;border-spacing: 15px 10px;">
    <tr>
      <th>Date</th>
      <td>Sunday, 14 December</td>
    </tr>
    <tr>
      <th>Venue</th>
      <td>Ūkaipō</td>
    </tr>
    <tr>
      <th>Time</th>
      <td>10:30am - 2:00pm</td>
    </tr>
  </table>
</p>
<p>
  Join us for our Annual Whānau Kirihimete Party. This year’s celebration will
  be filled with delicious kai, whakawhanaungatanga, and plenty of fun
  activities for the whole whānau. There will be bouncy castles, face painting,
  and the climbing wall for our tamariki.
</p>
<p>All whānau and all ages are welcome!</p>
<p>Gifts for tamariki aged 1–14 years</p>
<p>
  To ensure we have accurate numbers for catering and gifts, please register by
  completing this form.
</p>
<p>Registration closes <strong>Tuesday, 9 December</strong>.</p>
<p>
  If you have any pātai or need assistance, please contact our office on
  <a href="mailto:events@rangitane.org.n" target="_blank">
    events@rangitane.org.nz
  </a> or <a href="tel:+6435786180" target="_blank">(03) 578 6180</a>
</p>
          `,
          },
        ],
      },
      ...personalConfig,
      {
        name: "matePāwera",
        title: "Mate Pāwera - Allergies",
        questions: [
          {
            type: "radiogroup",
            name: "attendeeAllergies",
            title: "Do you have any allergies, including dietary requirements?",
            choices,
            isRequired: true,
          },
          {
            type: "text",
            name: "attendeeAllergyDetails",
            title:
              "Please list all allergies or dietary requirements, as well as medications required.",
            placeHolder: "Mate Pāwera",
            visibleIf: "{attendeeAllergies} = 'Āe'",
            isRequired: true,
          },
        ],
      },
      {
        name: "additionalAttendee",
        title: "He Hunga Anō - Additional Attendee",
        questions: [
          {
            type: "radiogroup",
            name: "attendee1Additional",
            title: "Are you bringing along any other whānau with you?",
            choices,
            isRequired: true,
          },
        ],
      },
      ...additionalAttendees,
      {
        name: "attendanceDetails",
        title: "Taipitopito Kaupapa - Attendance Details",
        questions: [
          {
            type: "radiogroup",
            name: "mediaRelease",
            title: `
              During our kirihimete party, we will be taking photos and videos
              which may be shared on our panui, website and/or our social media
              platforms. By agreeing with this statement, you give permission
              for Rangitāne o Wairau to share these photos or videos that may
              contain images of all those noted on this registration form.
            `,
            choices,
            isRequired: true,
          },
        ],
      },
      {
        name: "privacy",
        title: "Noho Matatapu - Privacy",
        elements: [
          {
            type: "html",
            html: `
<p>
  The information you provide on this form is being collected for
  the purpose of registering you for this event and for managing
  event logistics, communications, and follow-up activities.
</p>
<p>
  Your personal information will be handled in accordance with
  the Privacy Act 2020, which governs how personal data is
  collected, stored, used, and shared. It may be shared with
  event partners (such as funding partners) only where necessary
  for the delivery of the event or to meet our reporting and
  accountability obligations.
</p>
<p>
  Information collected will also form part of our organisational
  records and will be securely managed and retained in line with
  our record-keeping and privacy practices.
</p>
<p>
  You have the right to access and request correction of any
  personal information we hold about you. To do so, or if you
  have any other queries regarding your personal information,
  please contact us at
  <a href="mailto:admin@rangitane.org.nz" target="_blank">
    admin@rangitane.org.nz
  </a>
</p>
`,
          },
        ],
      },
      {
        name: "confirmation",
        title: "Kī Taurangi - Declaration",
        questions: [
          {
            type: "radiogroup",
            name: "physicalWork",
            title: `
              I confirm that all information provided in this form is true and
              correct.
            `,
            choices: aeChoices,
            isRequired: true,
          },
          signatureConfig[0].questions[0],
        ],
      },
    ],
  };
};
